/**
 * 权限管理页面
 * 展示用户-角色-菜单的关联关系，提供统一的权限管理界面
 */

import React, { useState, useEffect } from 'react';
import { Card, Tabs, Table, Tag, Space, Button, Modal, message, Tree, Select } from 'antd';
import { UserOutlined, TeamOutlined, MenuOutlined, SettingOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { userService } from '../../../services/userService';
import { roleService } from '../../../services/roleService';
import { menuService } from '../../../services/menuService';
import { permissionService } from '../../../services/permissionService';


interface User {
  id: number;
  username: string;
  nickname: string;
  roles: string[];
  roleIds: number[];
}

interface Role {
  id: number;
  roleName: string;
  roleKey: string;
  permissions: string[];
  menuIds: number[];
}

interface Menu {
  id: number;
  menuName: string;
  parentId: number;
  perms: string;
  children?: Menu[];
}

const PermissionManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [menuTree, setMenuTree] = useState<Menu[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [isUserRoleModalVisible, setIsUserRoleModalVisible] = useState(false);
  const [isRoleMenuModalVisible, setIsRoleMenuModalVisible] = useState(false);
  const [checkedKeys, setCheckedKeys] = useState<React.Key[]>([]);
  const [roleMenus, setRoleMenus] = useState<number[]>([]);

  // 用户表格列定义
  const userColumns: ColumnsType<User> = [
    {
      title: '用户名',
      dataIndex: 'username',
      key: 'username',
    },
    {
      title: '昵称',
      dataIndex: 'nickname',
      key: 'nickname',
    },
    {
      title: '角色',
      dataIndex: 'roles',
      key: 'roles',
      render: (roles: string[]) => (
        <Space wrap>
          {roles?.map(role => (
            <Tag key={role} color="blue">{role}</Tag>
          ))}
        </Space>
      ),
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Button 
          type="primary" 
          size="small"
          onClick={() => handleManageUserRoles(record)}
        >
          管理角色
        </Button>
      ),
    },
  ];

  // 角色表格列定义
  const roleColumns: ColumnsType<Role> = [
    {
      title: '角色名称',
      dataIndex: 'roleName',
      key: 'roleName',
    },
    {
      title: '角色标识',
      dataIndex: 'roleKey',
      key: 'roleKey',
    },
    {
      title: '权限',
      dataIndex: 'permissions',
      key: 'permissions',
      render: (permissions: string[]) => (
        <Space wrap>
          {permissions?.slice(0, 3).map(permission => (
            <Tag key={permission} color="green">{permission}</Tag>
          ))}
          {permissions?.length > 3 && <Tag color="default">+{permissions.length - 3}</Tag>}
        </Space>
      ),
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Button 
          type="primary" 
          size="small"
          onClick={() => handleManageRoleMenus(record)}
        >
          管理菜单
        </Button>
      ),
    },
  ];

  // 加载数据
  const loadData = async () => {
    setLoading(true);
    try {
      // 加载用户列表
      const userResponse = await userService.getList({ size: 1000 });
      if (userResponse.code === 200) {
        setUsers(userResponse.data.records || []);
      }

      // 加载角色列表
      const roleResponse = await roleService.getList({ size: 1000 });
      if (roleResponse.code === 200) {
        setRoles(roleResponse.data.records || []);
      }

      // 加载菜单树
      const menuResponse = await menuService.getList({ size: 1000 });
      if (menuResponse.code === 200) {
        const menus = menuResponse.data.records || [];
        const treeData = buildMenuTree(menus);
        setMenuTree(treeData);
      }
    } catch (error) {
      console.error('加载数据失败:', error);
      message.error('加载数据失败');
    } finally {
      setLoading(false);
    }
  };

  // 构建菜单树
  const buildMenuTree = (menus: Menu[]): Menu[] => {
    const map = new Map<number, Menu & { children: Menu[] }>();
    const roots: Menu[] = [];

    menus.forEach(menu => {
      map.set(menu.id, { ...menu, children: [] });
    });

    menus.forEach(menu => {
      const node = map.get(menu.id)!;
      if (menu.parentId === 0) {
        roots.push(node);
      } else {
        const parent = map.get(menu.parentId);
        if (parent) {
          parent.children.push(node);
        }
      }
    });

    return roots;
  };

  // 管理用户角色
  const handleManageUserRoles = (user: User) => {
    setSelectedUser(user);
    setIsUserRoleModalVisible(true);
  };

  // 管理角色菜单
  const handleManageRoleMenus = async (role: Role) => {
    setSelectedRole(role);
    setIsRoleMenuModalVisible(true);
    
    try {
      // 获取角色的菜单权限
      const response = await permissionService.getRoleMenus(role.id);
      if (response.code === 200) {
        const menuIds = response.data || [];
        setRoleMenus(menuIds);
        setCheckedKeys(menuIds.map((id: number) => String(id)));
      } else {
        console.error('获取角色菜单权限失败:', response.message);
        setRoleMenus([]);
        setCheckedKeys([]);
      }
    } catch (error) {
      console.error('获取角色菜单权限失败:', error);
      setRoleMenus([]);
      setCheckedKeys([]);
    }
  };

  // 保存角色菜单权限
  const handleSaveRoleMenus = async () => {
    if (!selectedRole) return;
    
    try {
      const menuIds = checkedKeys.map(key => Number(key));
      
      const response = await permissionService.assignRoleMenus(selectedRole.id, menuIds);
      if (response.code === 200) {
        message.success('权限保存成功');
        setIsRoleMenuModalVisible(false);
        // 重新加载数据
        loadData();
        // 触发菜单重新加载事件，让所有使用菜单的组件重新加载菜单
        // 这样分配了权限的用户登录后就能看到正确的菜单
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('menu:reload'));
          // 同时触发eventBus事件
          const { eventBus } = await import('../../../utils/eventBus');
          eventBus.emit('menu:changed');
        }
      } else {
        message.error('权限保存失败: ' + response.message);
      }
    } catch (error) {
      console.error('保存角色菜单权限失败:', error);
      message.error('权限保存失败');
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  return (
    <div style={{ padding: '20px' }}>
      <Card title="权限管理" extra={<SettingOutlined />}>
        <Tabs 
          defaultActiveKey="users"
          items={[
            {
              key: 'users',
              label: '用户角色管理',
              icon: <UserOutlined />,
              children: (
                <Table
                  columns={userColumns}
                  dataSource={users}
                  loading={loading}
                  rowKey="id"
                  pagination={false}
                />
              ),
            },
            {
              key: 'roles',
              label: '角色菜单管理',
              icon: <TeamOutlined />,
              children: (
                <Table
                  columns={roleColumns}
                  dataSource={roles}
                  loading={loading}
                  rowKey="id"
                  pagination={false}
                />
              ),
            },
            {
              key: 'menus',
              label: '菜单权限树',
              icon: <MenuOutlined />,
              children: (
                <Tree
                  treeData={menuTree.map(menu => ({
                    ...menu,
                    key: String(menu.id),
                    children: menu.children?.map(child => ({
                      ...child,
                      key: String(child.id),
                      children: child.children?.map(grandChild => ({
                        ...grandChild,
                        key: String(grandChild.id)
                      }))
                    }))
                  })) as any}
                  fieldNames={{ title: 'menuName', key: 'key', children: 'children' }}
                  defaultExpandAll
                  showLine
                />
              ),
            },
          ]}
        />
      </Card>

      {/* 用户角色管理模态框 */}
      <Modal
        title={`管理用户 ${selectedUser?.username} 的角色`}
        open={isUserRoleModalVisible}
        onCancel={() => setIsUserRoleModalVisible(false)}
        footer={null}
        width={600}
      >
        <div style={{ padding: '20px 0' }}>
          <p>当前用户角色：</p>
          <Space wrap>
            {selectedUser?.roles?.map(role => (
              <Tag key={role} color="blue">{role}</Tag>
            ))}
          </Space>
          <p style={{ marginTop: '20px' }}>选择新角色：</p>
          <Select
            mode="multiple"
            style={{ width: '100%' }}
            placeholder="请选择角色"
            options={roles.map(role => ({
              label: `${role.roleName} (${role.roleKey})`,
              value: role.id
            }))}
            defaultValue={selectedUser?.roleIds}
          />
        </div>
      </Modal>

      {/* 角色菜单管理模态框 */}
      <Modal
        title={`管理角色 ${selectedRole?.roleName} 的菜单权限`}
        open={isRoleMenuModalVisible}
        onCancel={() => setIsRoleMenuModalVisible(false)}
        onOk={handleSaveRoleMenus}
        okText="保存"
        cancelText="取消"
        width={600}
      >
        <div style={{ padding: '20px 0' }}>
          <p>当前角色权限：</p>
          <Space wrap>
            {selectedRole?.permissions?.map(permission => (
              <Tag key={permission} color="green">{permission}</Tag>
            ))}
          </Space>
          <p style={{ marginTop: '20px' }}>选择菜单权限：</p>
          <Tree
            checkable
            treeData={menuTree.map(menu => ({
              ...menu,
              key: String(menu.id),
              children: menu.children?.map(child => ({
                ...child,
                key: String(child.id),
                children: child.children?.map(grandChild => ({
                  ...grandChild,
                  key: String(grandChild.id)
                }))
              }))
            })) as any}
            fieldNames={{ title: 'menuName', key: 'key', children: 'children' }}
            defaultExpandAll
            checkedKeys={checkedKeys}
            onCheck={(checkedKeys, info) => {
              setCheckedKeys(checkedKeys as React.Key[]);
            }}
            style={{ maxHeight: 300, overflow: 'auto' }}
          />
        </div>
      </Modal>
    </div>
  );
};

export default PermissionManagement;